import React, { useEffect, useState } from 'react';
import {
  Typography, Table, Button, Modal, Form, Input, InputNumber, Select, Switch, Space, Tag, Popconfirm, message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../api';

// Admin > Business Config > WFM Skills Config: the special skills people hold
// for scheduling (Nopec, RI, Chat ...), each with its teams (where Campaign
// Updates offers it), campaigns and Paycor shift departments. Given to people on WFM Skills Assign. admin and WFM
// Skills Edit Manager (routes/wfm_skills.py).
export default function WFMSkillsConfigPage() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState({ teams: [], campaigns: [], shift_departments: [] });
  const [modal, setModal]     = useState(null);   // null | { skill }
  const [saving, setSaving]   = useState(false);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    api.get('/api/wfm-skills')
      .then((r) => setRows(r.data || []))
      .catch(() => message.error('Failed to load WFM skills.'))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get('/api/wfm-skills/options').then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  // Fill the form for the skill clicked, every time the popup opens: the form
  // instance outlives the popup, so its starting values alone could show the
  // skill edited before.
  useEffect(() => {
    if (!modal) return;
    const s = modal.skill;
    form.resetFields();
    form.setFieldsValue({
      name: s?.Name || '',
      description: s?.Description || '',
      team_ids: (s?.Teams || []).map((t) => t.ID),
      campaign_ids: (s?.Campaigns || []).map((c) => c.ID),
      shift_departments: s?.ShiftDepartments || [],
      sort_order: s ? s.SortOrder : (rows.length ? Math.max(...rows.map((r) => r.SortOrder)) + 1 : 1),
      is_active: s ? s.IsActive : true,
    });
  }, [modal, form]); // eslint-disable-line react-hooks/exhaustive-deps

  const body = (s, over = {}) => ({
    name: s.Name, description: s.Description, team_ids: s.Teams.map((t) => t.ID), campaign_ids: s.Campaigns.map((c) => c.ID),
    shift_departments: s.ShiftDepartments, sort_order: s.SortOrder, is_active: s.IsActive, ...over,
  });

  const save = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      if (modal.skill) await api.put(`/api/wfm-skills/${modal.skill.ID}`, v);
      else             await api.post('/api/wfm-skills', v);
      setModal(null);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (s, on) => {
    try { await api.put(`/api/wfm-skills/${s.ID}`, body(s, { is_active: on })); load(); }
    catch (err) { message.error(err.response?.data?.error || 'Failed to update.'); }
  };
  const remove = async (s) => {
    try { await api.delete(`/api/wfm-skills/${s.ID}`); load(); }
    catch (err) { message.error(err.response?.data?.error || 'Delete failed.'); }
  };

  // Inactive teams and campaigns only when the skill already has them.
  const keptTeams = (modal?.skill?.Teams || []).map((t) => t.ID);
  const teamOptions = options.teams.filter((t) => t.Status === 'active' || keptTeams.includes(t.ID))
    .map((t) => ({ value: t.ID, label: t.Name }));
  const kept = (modal?.skill?.Campaigns || []).map((c) => c.ID);
  const campaignOptions = options.campaigns.filter((c) => c.IsActive || kept.includes(c.ID))
    .map((c) => ({ value: c.ID, label: c.Name }));
  const deptOptions = [...new Set([...options.shift_departments, ...(modal?.skill?.ShiftDepartments || [])])]
    .sort((a, b) => a.localeCompare(b)).map((x) => ({ value: x, label: x }));
  const tags = (items) => <Space size={[4, 4]} wrap>{items.map((x) => <Tag key={x}>{x}</Tag>)}</Space>;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>WFM Skills Config</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModal({ skill: null })}>Add WFM Skill</Button>
      </div>
      <Table
        rowKey="ID"
        dataSource={rows}
        loading={loading}
        size="small"
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No WFM skills yet.' }}
        columns={[
          { title: 'Order', dataIndex: 'SortOrder', key: 'SortOrder', width: 70, align: 'right',
            sorter: (a, b) => a.SortOrder - b.SortOrder },
          { title: 'Name', dataIndex: 'Name', key: 'Name', width: 160,
            sorter: (a, b) => a.Name.localeCompare(b.Name) },
          { title: 'Description', dataIndex: 'Description', key: 'Description', width: 260,
            render: (v) => v || '—' },
          { title: 'Teams', dataIndex: 'Teams', key: 'Teams', width: 200,
            render: (v) => tags(v.map((t) => t.Name)) },
          { title: 'Campaigns', dataIndex: 'Campaigns', key: 'Campaigns', width: 220,
            render: (v) => tags(v.map((c) => c.Name)) },
          { title: 'Shift Departments', dataIndex: 'ShiftDepartments', key: 'ShiftDepartments', width: 240,
            render: (v) => tags(v) },
          { title: 'Active', dataIndex: 'IsActive', key: 'IsActive', width: 80,
            render: (v, s) => <Switch size="small" checked={!!v} onChange={(on) => toggleActive(s, on)} /> },
          { title: '', key: 'actions', width: 120, align: 'right',
            render: (_, s) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => setModal({ skill: s })}>Edit</Button>
                <Popconfirm title="Delete this WFM skill?" okText="Delete" okButtonProps={{ danger: true }}
                  onConfirm={() => remove(s)}>
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ) },
        ]}
      />

      <Modal maskClosable={false}
        title={modal?.skill ? 'Edit WFM Skill' : 'Add WFM Skill'}
        open={!!modal}
        onOk={save}
        onCancel={() => setModal(null)}
        confirmLoading={saving}
        okText="Save"
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false} style={{ marginTop: 12 }}>
          <Form.Item label="Name" name="name" rules={[{ required: true, whitespace: true, message: 'Name is required' }]}>
            <Input maxLength={50} />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea maxLength={500} autoSize={{ minRows: 2, maxRows: 6 }} />
          </Form.Item>
          <Form.Item label="Teams" name="team_ids"
            rules={[{ required: true, type: 'array', min: 1, message: 'Pick at least one team' }]}>
            <Select mode="multiple" allowClear showSearch optionFilterProp="label" options={teamOptions} />
          </Form.Item>
          <Form.Item label="Campaigns" name="campaign_ids"
            rules={[{ required: true, type: 'array', min: 1, message: 'Pick at least one campaign' }]}>
            <Select mode="multiple" allowClear showSearch optionFilterProp="label" options={campaignOptions} />
          </Form.Item>
          <Form.Item label="Shift Departments" name="shift_departments"
            rules={[{ required: true, type: 'array', min: 1, message: 'Pick at least one shift department' }]}>
            <Select mode="multiple" allowClear showSearch optionFilterProp="label" options={deptOptions} />
          </Form.Item>
          <Space size={24}>
            <Form.Item label="Display Order" name="sort_order">
              <InputNumber min={0} precision={0} style={{ width: 100 }} />
            </Form.Item>
            <Form.Item label="Active" name="is_active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </>
  );
}
